'use client';

/**
 * WebQACapabilityCard — Reception capability lane (klyde_ask, Step 3).
 *
 * Reads `useReceptionAskStore.capability` — the compositor's capability facet
 * (ki_ask), scope-clamped to the buyer. Renders the agent's narrative + key
 * findings inline, and — when the picked capability shipped a renderer_card
 * (e.g. home_pipeline, intent_results) — an "Open card in canvas" affordance
 * that mounts it in the AgentCanvasOverlay (which already warms + skeletons the
 * heavy card). Mirrors the rail's CapabilityLane; renders nothing when the
 * question was knowledge-only (no operational lane fired).
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { Wrench, Maximize2 } from 'lucide-react';
import { useReceptionAskStore } from '@/lib/stores/reception-ask-store';
import { useAgentCanvasStore } from '@/lib/stores/agent-canvas-store';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import { QA_SECTION, QA_BOX } from './web-qa-chrome';
import type { AskView } from '@/lib/api/ask-types';

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

export function WebQACapabilityCard() {
  const T = useCardTypography();
  const capability = useReceptionAskStore((s) => s.capability);
  const openCompose = useAgentCanvasStore((s) => s.openCompose);

  if (!capability) return null;

  const p = capability.payload;
  const body = str(p.narrative) ?? str(p.clarification) ?? (str(p.status) ? `(${p.status})` : null);
  const findings = Array.isArray(p.key_findings)
    ? p.key_findings.filter((f): f is string => typeof f === 'string')
    : [];
  const card = str(p.renderer_card);
  // A minimal CLEAN view carrying just this capability's card, so the migrated overlay
  // (which reads compose.view) mounts it. Reception is a single card — no quick/synthesis.
  const flipView: AskView = {
    query: null, quick: null, llm_status: null, synthesis: null,
    sa_read: null, sa_refs: null, sa_offered: null, sa_key: null,
    answer_state: null, followups: [],
    card: {
      renderer_card: card,
      entity_sid: str(p.renderer_entity_sid),
      entity_type: str(p.renderer_entity_type),
      data: p.data,
    },
    cards: null, navigate: null, picked: null,
  };

  // Nothing operational to show — stay quiet (the knowledge lane carries the answer).
  if (!body && findings.length === 0 && !card) return null;

  return (
    <section className={QA_SECTION}>
      <div className="w-full">
        <div className={QA_BOX}>
          <div className={cn(T.metadata, 'uppercase tracking-wider text-xs flex items-center gap-1.5')}>
            <Wrench className="h-3 w-3" />
            Capability
          </div>

          {body && (
            <p className={cn(T.body, 'leading-relaxed whitespace-pre-line')}>{body}</p>
          )}

          {findings.length > 0 && (
            <ul className={cn(T.body, 'list-disc pl-5 flex flex-col gap-1 text-muted-foreground')}>
              {findings.map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          )}

          {card && (
            <button
              onClick={() => openCompose({ facets: [capability], view: flipView })}
              className="mt-1 inline-flex items-center gap-1.5 self-start rounded-md border border-primary/30 bg-primary/5 px-2.5 py-1.5 text-sm text-primary hover:bg-primary/10 transition-colors"
              title={`Open ${card} in canvas`}
            >
              <Maximize2 className="h-3.5 w-3.5" />
              Open card in canvas
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
